import * as Yup from 'yup';

export function cleanupSwedishNationalId(ssn: string): string {
    const trimmed = ssn.trim();
    if (trimmed.length > 8 && trimmed.charAt(8) === '-') {
        return trimmed.substring(0, 8) + trimmed.substring(9);
    }
    return trimmed;
}

export const swedishNationalId = () =>
    Yup.string()
        .nullable()
        .typeError('Fältet är obligatoriskt')
        .required('Fältet är obligatoriskt')
        .test(
            'test-swedishnationalid', // this is used internally by yup
            'Felaktigt personnummer - 10 siffror ååmmdd(-/+)xxxx', //validation message
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            (value) => {
                if (!value) {
                    return false;
                }

                const res = parseSwedishSsn(value);
                //console.log(res);
                return res !== null;
            }
        );

const SSN_RE = /^(?<year>(\d{2}|\d{4}))(?<month>\d{2})(?<day>\d{2})(?<delimiter>(-|\+))?(?<serial>\d{3})(?<check>\d)$/;
const INSIGNIFICANT_CHARS_RE = /"[^\d+-]/;

export function parseSwedishSsn(input?: string) {
    const cleaned = input ? input.replace(INSIGNIFICANT_CHARS_RE, '') : '';
    const m = SSN_RE.exec(cleaned);
    if (m) {
        const delimiter = m.groups?.['delimiter'];
        const year = yearFromParts(m.groups?.['year'] ?? '', delimiter);
        const month = parseInt(m.groups?.['month'] ?? '', 10);
        const day = parseInt(m.groups?.['day'] ?? '', 10);
        const serial = m.groups?.['serial'];
        const checkDigit = m.groups?.['check'];
        const isCoordinationNumber = day > 31;

        const dob = new Date(year, month - 1, isCoordinationNumber ? day - 60 : day);
        const shortFormat = `${pad(year % 100)}${pad(month)}${pad(day)}${serial}${checkDigit}`;

        if (isValidLuhn(shortFormat)) {
            return {
                dob,
                gender: parseInt(serial?.[2] ?? '', 10) % 2 == 0 ? 'Male' : 'Female',
                isCoordinationNumber,
                structuredFormat: `${year}${pad(month)}${pad(day)}${serial}${checkDigit}`,
                officialFormat: `${pad(year % 100)}${pad(month)}${pad(day)}${getDelimter(dob)}${serial}${checkDigit}`,
                shortFormat,
            };
        }
    }
    return null;
}

function pad(num: number) {
    return `00${num}`.slice(-2);
}

function isValidLuhn(shortFormatSsn: string) {
    if (shortFormatSsn.length != 10) {
        throw new Error('shortFormatSsn must be 10 digits');
    }
    const digits = shortFormatSsn.split('').map((n) => parseInt(n, 10));
    const check = digits.pop() as number;
    const sum = digits.reduce((m, c, i) => {
        let prod = c * (2 - (i % 2));
        if (prod > 9) {
            prod = 1 + (prod % 10);
        }
        return m + prod;
    }, 0);
    return (check + sum) % 10 == 0;
}

function yearFromParts(year: string, delimiter?: string) {
    const currentYear = new Date().getFullYear();
    let birthYear;
    if (year.length == 4) {
        birthYear = parseInt(year, 10);
    } else {
        const decade = parseInt(year, 10);
        const currentDecade = currentYear % 100;
        if (delimiter == '+') {
            birthYear = decade > currentDecade ? 1800 + decade : 1900 + decade;
        } else {
            birthYear = decade > currentDecade ? 1900 + decade : 2000 + decade;
        }
    }

    return birthYear;
}

function getDelimter(dob: Date) {
    return getAgeJanuary1st(new Date(dob.getTime())) >= 100 ? '+' : '-';
}

function getAgeJanuary1st(birthDate: Date) {
    const january1stThisYear = new Date(new Date().getFullYear(), 1, 1);

    let age = january1stThisYear.getFullYear() - birthDate.getFullYear();
    if (january1stThisYear.getTime() < birthDate.setFullYear(birthDate.getFullYear() + age)) age--;
    return age;
}
